import React, { useEffect, useState } from 'react';
import { IAnalyticsFilters, IAnalyticsCustomersResponse } from '@/interfaces/Dashboard/dashboard.interface';
import { AnalyticsExportButton } from './AnalyticsExportButton';
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { Users, UserPlus, UserCheck, Activity, Heart, Repeat, ShoppingBag, DollarSign, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { CommonCardSkeleton, LineChartSkeleton } from '@/components/ui/Loaders/DashboardSkeletons';
import { EmptyChart } from '@/components/common/EmptyChart';
import { NoDataFound } from '@/components/common/NoDataFound';
import CommonTable from '@/components/common/CommonTable';
import { formatRand, formatChartDate } from '@/utility/helper';

interface CustomersSectionProps {
    filters: IAnalyticsFilters;
    data: IAnalyticsCustomersResponse['data'] | null;
    isLoading: boolean;
}


export const CustomersSection: React.FC<CustomersSectionProps> = ({ filters, data, isLoading }) => {

    if (isLoading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="flex justify-between items-center mb-4">
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => <CommonCardSkeleton key={i} />)}
                </div>
            </div>
        );
    }

    const kpis = data?.kpis || {} as any;
    const metrics = data?.metrics || {} as any;
    const charts = data?.charts || {} as any;
    const tables = data?.tables || {} as any;

    const kpiCards = [
        { label: 'Total Customers', value: kpis.totalCustomers ?? 0, icon: <Users />, color: '#3B82F6' },
        { label: 'New Customers', value: kpis.newCustomers ?? 0, icon: <UserPlus />, color: '#10B981' },
        { label: 'Returning Customers', value: kpis.returningCustomers ?? 0, icon: <Repeat />, color: '#8B5CF6' },
        { label: 'Active Customers', value: kpis.activeCustomers ?? 0, icon: <Activity />, color: '#EC4899' },
    ];

    const metricCards = [
        { label: 'Retention Rate', value: `${metrics.retentionRate ?? 0}%`, icon: <Heart />, color: '#EF4444' },
        { label: 'Repeat Purchase Rate', value: `${metrics.repeatPurchaseRate ?? 0}%`, icon: <Repeat />, color: '#8B5CF6' },
        { label: 'Avg Orders / Customer', value: metrics.avgOrdersPerCustomer ?? 0, icon: <ShoppingBag />, color: '#3B82F6' },
        { label: 'Customer Lifetime Value', value: formatRand(metrics.clv), icon: <DollarSign />, color: '#10B981' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-foreground">Customer Growth</h2>
                <AnalyticsExportButton
                    exportUrl="/reports/analytics/export?type=customers"
                    fileName="customers_report.xlsx"
                    filters={filters}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {kpiCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {metricCards.map((card, i) => (
                    <CommonCard key={i} label={card.label} value={card.value} icon={card.icon} iconColor={card.color} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard title="Customer Growth" icon={<TrendingUp />}>
                    <div className="h-[300px] w-full">
                        {!charts.customerGrowthTrend || charts.customerGrowthTrend.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={charts.customerGrowthTrend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Area type="monotone" dataKey="total" name="Total Customers" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={3} />
                                    <Area type="monotone" dataKey="new" name="New Customers" stroke="#10B981" fill="#10B981" fillOpacity={0.4} strokeWidth={2} />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                </AreaChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>

                <SectionCard title="New vs Returning Customers" icon={<Users />}>
                    <div className="h-[300px] w-full">
                        {!charts.newVsReturning || charts.newVsReturning.length === 0 ? (
                            <EmptyChart height="h-[300px]" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={charts.newVsReturning}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={formatChartDate} interval={0} angle={-45} textAnchor="end" height={60} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        cursor={{ fill: '#F3F4F6' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar dataKey="returning" name="Returning" stackId="a" fill="#8B5CF6" barSize={32} />
                                    <Bar dataKey="new" name="New" stackId="a" fill="#10B981" radius={[4, 4, 0, 0]} barSize={32} />
                                    <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </SectionCard>
            </div>

            <SectionCard title="Top Customers" className="overflow-x-auto">
                <CommonTable
                    columns={[
                        { key: 'name', header: 'Customer Name', className: 'font-medium text-foreground' },
                        { key: 'email', header: 'Email', className: 'text-muted-foreground' },
                        { key: 'orders', header: 'Orders', className: 'text-right', headerClassName: 'text-right' },
                        { key: 'totalSpent', header: 'Total Spent', render: (row: any) => formatRand(row.totalSpent), className: 'text-right font-bold text-success', headerClassName: 'text-right' }
                    ]}
                    data={tables.topCustomers || []}
                />
            </SectionCard>
        </div>
    );
};
